import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { Plus, Pencil, Trash2, Upload, FileText, Info, X } from 'lucide-react';
import { toast } from 'sonner';
import SearchBar from '../components/SearchBar';

const REG_TYPES = ['GST', 'MSME', 'Others'];

export default function RegistrationPage() {
  const [records, setRecords] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [typeFilter, setTypeFilter] = useState('');
  const [searchText, setSearchText] = useState('');
  const [form, setForm] = useState({ client_id: '', client_name: '', registration_type: '', applied_date: '', approval_date: '', certificate_received: false, notes: '' });
  const [uploadFiles, setUploadFiles] = useState([]);
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (typeFilter) params.set('reg_type', typeFilter);
      if (searchText) params.set('search', searchText);
      const [recRes, clRes] = await Promise.all([api.get(`/registrations?${params}`), api.get('/clients?limit=500')]);
      setRecords(recRes.data);
      setClients(clRes.data.clients || []);
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  }, [typeFilter, searchText]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openNew = () => {
    setEditId(null);
    setForm({ client_id: '', client_name: '', registration_type: 'GST', applied_date: '', approval_date: '', certificate_received: false, notes: '' });
    setUploadFiles([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
    setDialogOpen(true);
  };

  const openEdit = (rec) => {
    setEditId(rec._id);
    setForm({ client_id: rec.client_id, client_name: rec.client_name, registration_type: rec.registration_type, applied_date: rec.applied_date || '', approval_date: rec.approval_date || '', certificate_received: rec.certificate_received || false, notes: rec.notes || '' });
    setUploadFiles([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
    setDialogOpen(true);
  };

  const uploadRegistrationDocs = async (clientId, regType) => {
    if (!uploadFiles.length || !clientId) return;
    setUploading(true);
    try {
      for (const file of uploadFiles) {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('client_id', clientId);
        fd.append('document_type', `Registration - ${regType}`);
        await api.post('/documents/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      toast.success(`${uploadFiles.length} document(s) saved to client`);
    } catch {
      toast.error('Some documents failed to upload');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.client_name || !form.registration_type) { toast.error('Client and type required'); return; }
    if (uploadFiles.length && !form.client_id) { toast.error('Please select a client before uploading documents'); return; }
    try {
      if (editId) { await api.put(`/registrations/${editId}`, form); toast.success('Updated'); }
      else { await api.post('/registrations', form); toast.success('Created'); }
      if (uploadFiles.length) {
        await uploadRegistrationDocs(form.client_id, form.registration_type);
      }
      setDialogOpen(false);
      setUploadFiles([]);
      fetchData();
    } catch { toast.error('Failed to save'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete?')) return;
    try { await api.delete(`/registrations/${id}`); toast.success('Deleted'); fetchData(); } catch { toast.error('Failed'); }
  };

  const onFilesPicked = (e) => {
    const files = Array.from(e.target.files || []);
    setUploadFiles(prev => [...prev, ...files]);
    e.target.value = '';
  };
  const removeFile = (idx) => setUploadFiles(prev => prev.filter((_, i) => i !== idx));

  return (
    <div data-testid="registrations-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-4xl font-semibold text-[#111B11] tracking-tight">Registration Tracking</h1>
          <p className="text-sm text-[#4F734F] mt-1">Track client registration statuses</p>
        </div>
        <Button onClick={openNew} className="btn-accent gap-2" data-testid="add-reg-btn"><Plus size={16} /> New Registration</Button>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <SearchBar
          value={searchText}
          onChange={setSearchText}
          placeholder="Search client..."
          testId="reg-search"
          minWidth="min-w-[280px]"
          className="!flex-none w-full sm:w-80"
        />
        <Select value={typeFilter || 'all'} onValueChange={(v) => setTypeFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-40 h-11 input-field"><SelectValue placeholder="All Types" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Types</SelectItem>{REG_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="bg-white border border-[#E6EDE6] rounded-sm overflow-hidden">
        <table className="data-table w-full">
          <thead><tr><th>Client</th><th>Type</th><th>Applied Date</th><th>Approval Date</th><th>Certificate</th><th>Notes</th><th>Actions</th></tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="text-center py-8 text-olive-400">Loading...</td></tr>
            ) : records.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-8 text-olive-400">No records found</td></tr>
            ) : records.map(rec => (
              <tr key={rec._id}>
                <td className="font-medium text-[#111B11]">{rec.client_name}</td>
                <td><span className="bg-olive-100 text-olive-700 rounded-full px-2 py-0.5 text-xs">{rec.registration_type}</span></td>
                <td>{rec.applied_date || '-'}</td>
                <td>{rec.approval_date || '-'}</td>
                <td>{rec.certificate_received ? <span className="badge-success">Yes</span> : <span className="badge-warning">No</span>}</td>
                <td className="max-w-[200px] truncate">{rec.notes || '-'}</td>
                <td>
                  <div className="flex gap-1">
                    <button onClick={() => openEdit(rec)} className="p-1.5 hover:bg-olive-50 rounded-sm"><Pencil size={14} className="text-olive-500" /></button>
                    <button onClick={() => handleDelete(rec._id)} className="p-1.5 hover:bg-red-50 rounded-sm"><Trash2 size={14} className="text-red-500" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading text-xl">{editId ? 'Edit' : 'New'} Registration</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase font-semibold text-[#4F734F]">Client</Label>
              <Select value={form.client_id || 'none'} onValueChange={(v) => { const c = clients.find(cl => cl._id === v); setForm(prev => ({ ...prev, client_id: v, client_name: c?.full_name || '' })); }}>
                <SelectTrigger className="input-field"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent><SelectItem value="none" disabled>Select</SelectItem>{clients.map(c => <SelectItem key={c._id} value={c._id}>{c.full_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase font-semibold text-[#4F734F]">Registration Type</Label>
              <Select value={form.registration_type} onValueChange={(v) => setForm(prev => ({ ...prev, registration_type: v }))}>
                <SelectTrigger className="input-field"><SelectValue /></SelectTrigger>
                <SelectContent>{REG_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label className="text-xs uppercase font-semibold text-[#4F734F]">Applied Date</Label><Input type="date" value={form.applied_date} onChange={(e) => setForm(prev => ({ ...prev, applied_date: e.target.value }))} className="input-field" /></div>
              <div className="space-y-1.5"><Label className="text-xs uppercase font-semibold text-[#4F734F]">Approval Date</Label><Input type="date" value={form.approval_date} onChange={(e) => setForm(prev => ({ ...prev, approval_date: e.target.value }))} className="input-field" /></div>
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={form.certificate_received} onCheckedChange={(v) => setForm(prev => ({ ...prev, certificate_received: v }))} data-testid="cert-switch" />
              <Label className="text-sm text-[#273A27]">Certificate Received</Label>
            </div>
            <div className="space-y-1.5"><Label className="text-xs uppercase font-semibold text-[#4F734F]">Notes</Label><Textarea value={form.notes} onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))} className="input-field" /></div>

            {/* Document Upload */}
            <div className="space-y-2 pt-2 border-t border-[#E6EDE6]">
              <Label className="text-xs uppercase font-semibold text-[#4F734F]">Attach Registration Documents (optional)</Label>
              <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2 text-xs text-amber-900">
                <Info size={14} className="mt-0.5 flex-shrink-0 text-amber-600" />
                <span>Please upload <strong>only</strong> the documents used to apply for this registration. Files are saved under the selected client&apos;s Documents.</span>
              </div>
              <div className="flex items-center gap-2">
                <label className="inline-flex items-center gap-1.5 border border-[#D5DFD5] bg-white hover:bg-[#F8FAF7] rounded-sm px-3 py-2 text-sm text-[#273A27] cursor-pointer" data-testid="reg-upload-label">
                  <Upload size={14} />
                  <span>Choose files</span>
                  <input ref={fileInputRef} type="file" multiple accept="image/*,application/pdf" className="hidden" onChange={onFilesPicked} data-testid="reg-upload-input" />
                </label>
                {uploadFiles.length > 0 && <span className="text-xs text-[#4F734F]">{uploadFiles.length} file(s) selected</span>}
              </div>
              {uploadFiles.length > 0 && (
                <div className="space-y-1 max-h-32 overflow-y-auto" data-testid="reg-upload-list">
                  {uploadFiles.map((f, i) => (
                    <div key={i} className="flex items-center justify-between bg-[#F8FAF7] border border-[#E6EDE6] rounded-sm px-2 py-1 text-xs">
                      <span className="flex items-center gap-1.5 truncate"><FileText size={11} className="text-[#D4AF37]" />{f.name}</span>
                      <button type="button" onClick={() => removeFile(i)} className="p-0.5 hover:bg-red-50 rounded-full"><X size={11} className="text-red-500" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex gap-3 pt-2">
              <Button onClick={handleSubmit} disabled={uploading} className="btn-primary" data-testid="save-reg-btn">{uploading ? 'Saving...' : (editId ? 'Update' : 'Create')}</Button>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
